// Copyright Epic Games, Inc. All Rights Reserved.

import { json } from "@remix-run/cloudflare";
import {
	isRouteErrorResponse,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useNavigation,
	useRouteError,
	useRouteLoaderData,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";

import { NotFound } from "~/components/NotFound";

import bulma from "./bulma.css?url";
import { AlternateHrefLang } from "./components/AlternateHrefLang";
import LoadingAnimation from "./components/LoadingAnimation";
import TrackingJs from "./components/TrackingJs";
import { getSdk } from "./generated/schema.graphcms";
import global from "./globals.scss?url";
import GlobalContextProvider from "./routes/_index/components/context/GlobalContext";
import { Footer } from "./routes/_index/components/footer/Footer";
import Navbar from "./routes/_index/components/header/Navbar";
import { getGraphCms, getStage } from "./services/graphcms";
import typography from "./typography.css?url";
import { getDictionary } from "./utils/dictionaries";
import { localeTokenMap } from "./utils/localeTokenMap";

import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";

import type { GetNavigationQuery, Locale } from "./generated/schema.graphcms";
import type { Locale as AppLocale } from "./i18n.config";
import type { Dictionary } from "./utils/dictionaries";
// import LoadingAnimation from "./components/LoadingAnimation";

const EPIC_COMPONENTS_DOMAIN = "https://components.unrealengine.com";

export const links: LinksFunction = () => [
	{ rel: "stylesheet", href: bulma },
	{ rel: "stylesheet", href: global },
	{ rel: "stylesheet", href: typography },
];

export function Layout({ children }: { children: React.ReactNode }): React.ReactNode {
	const { pathname } = useLocation();

	// Use useRouteLoaderData instead of useLoaderData - it returns null in error boundaries
	const data = useRouteLoaderData<typeof loader>("root");
	const epic_components_domain = data?.epic_components_domain || EPIC_COMPONENTS_DOMAIN;

	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Meta />
				<Links />
				<AlternateHrefLang pathname={pathname} />
				<script
					type="module"
					crossOrigin="anonymous"
					src={`${epic_components_domain}/footer/sideswipe-footer.mjs`}
				></script>
			</head>
			<body className={`rl-sideswipe max-width`} style={{ overflowX: "hidden" }}>
				{children}
				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

function App(): React.ReactNode {
	const { navdata, epic_hostname, locale } = useLoaderData<typeof loader>();
	const { state } = useNavigation();

	return (
		<GlobalContextProvider locale={locale}>
			<Navbar config={navdata?.header} />
			{state === "loading" ? <LoadingAnimation /> : <Outlet />}
			<Footer locale={locale} domain={epic_hostname} />
			<TrackingJs />
		</GlobalContextProvider>
	);
}

export default withSentry(App);

async function getNavData(env: Env, mappedLocaleToken: string): Promise<GetNavigationQuery> {
	const { GetNavigation } = getSdk(getGraphCms(env));

	const globalPageData = await GetNavigation({
		locale: [mappedLocaleToken, "en"] as Locale[],
		appId: "sideswipe",
		stage: getStage(env),
	});

	return globalPageData;
}

export const loader = async ({ context }: LoaderFunctionArgs) => {
	const { env } = context.cloudflare;
	const { locale } = context;
	const epic_hostname = env.EPIC_HOSTNAME;
	const epic_components_domain = env.EPIC_COMPONENTS_DOMAIN;
	const mappedLocaleToken = localeTokenMap(locale);

	const navdata = await getNavData(env, mappedLocaleToken);

	const dict = await getDictionary(locale);

	return json<{
		navdata: GetNavigationQuery;
		dict: Dictionary;
		epic_hostname: string;
		epic_components_domain: string;
		locale: AppLocale;
	}>({
		navdata,
		dict,
		epic_hostname,
		epic_components_domain,
		locale,
	});
};

export function ErrorBoundary(): React.ReactNode {
	const error = useRouteError();

	if (isRouteErrorResponse(error) && error.status === 404) {
		return <NotFound />;
	}

	captureRemixErrorBoundaryError(error);

	return (
		<div>
			<h1>Oops!</h1>
		</div>
	);
}
